import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { socket_url } from "./constant";

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
    const [stompClient, setStompClient] = useState(null);
    const stompClientRef = useRef(null);
    const location = useLocation();
    const subscribers = useRef(new Set());

    useEffect(() => {
        if (location.pathname !== "/" && !stompClientRef.current) {
            // console.log("🔌 Initializing WebSocket connection...");

            let secureSocketUrl = socket_url;
            if (window.location.protocol === "https:" && secureSocketUrl.startsWith("http:")) {
                secureSocketUrl = secureSocketUrl.replace("http:", "https:");
            }
            const socket = new SockJS(secureSocketUrl);
            const client = new Client({
                webSocketFactory: () => socket,
                reconnectDelay: 5000,
                debug: (str) => {
                    // console.debug("🧩 [WebSocket Debug]", str);
                },
                onConnect: () => {
                    // console.log("✅ WebSocket connected successfully");
                    subscribeToWebSocket(client);
                },
                onStompError: (frame) => {
                    // console.error("❌ STOMP error:", frame.headers["message"]);
                    // console.error("🧾 Details:", frame.body);
                },
                onWebSocketError: (event) => {
                    // console.error("❌ WebSocket transport error:", event);
                }
            });

            client.activate();
            stompClientRef.current = client;
            setStompClient(client);

            return () => {
                // console.log("🔌 Cleaning up WebSocket connection...");
                client.deactivate();
                stompClientRef.current = null;
                subscribers.current.clear();
                // console.log("🧹 Subscribers cleared");
            };
        }
    }, [location.pathname]);

    const subscribeToWebSocket = (client) => {
        // console.log("📡 Subscribing to /topic/threads...");
        client.subscribe("/topic/threads", (message) => {
            try {
                const data = JSON.parse(message.body);
                // console.log("📬 New message received:", data);
                subscribers.current.forEach(callback => callback(data));
            } catch (e) {
                // console.error("❌ Error parsing WebSocket message:", e);
            }
        });
        // console.log("✅ Subscribed to /topic/threads");
    };

    const subscribeToTopic = (callback) => {
        subscribers.current.add(callback);
        // console.log(`➕ New subscriber added. Total: ${subscribers.current.size}`);
    };

    const disconnectWebSocket = () => {
        if (stompClientRef.current) {
            // console.log("❎ Disconnecting WebSocket...");
            stompClientRef.current.deactivate();
            stompClientRef.current = null;
            setStompClient(null);
            subscribers.current.clear();
            // console.log("✅ WebSocket disconnected and subscribers cleared");
        } else {
            // console.warn("⚠️ No active WebSocket connection to disconnect");
        }
    };

    return (
        <WebSocketContext.Provider value={{ subscribeToTopic, disconnectWebSocket }}>
            {children}
        </WebSocketContext.Provider>
    );
};

export const useWebSocket = () => useContext(WebSocketContext);
